import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Building2, 
  Target, 
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Download,
  FileText,
  Shuffle,
  ChevronDown,
  Star,
  Zap,
  TrendingUp,
  Users,
  Clock,
  Shield,
  Award,
  Lightbulb,
  // Additional icons for external services
  Github,
  Slack,
  FileSpreadsheet,
  FileText as FileDocument,
  FileCode,
  Calendar,
  MessageSquare,
  Trello as TrelloIcon,
  Info,
  HelpCircle
} from 'lucide-react';
import formPresets from '../../data/formPresets.json';

interface RoadmapData {
  companyName: string;
  industry: string;
  companyDescription: string;
  companyWebsite: string;
  objective: string;
  challenges: string;
  constraints: string;
  successCriteria: string;
  timeline: string;
  budget: string;
  roadmapFile: File | null;
}

const RoadmapInnovation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RoadmapData>({
    companyName: '',
    industry: '',
    companyDescription: '',
    companyWebsite: '',
    objective: '',
    challenges: '',
    constraints: '',
    successCriteria: '',
    timeline: '',
    budget: '',
    roadmapFile: null
  });

  const [dragActive, setDragActive] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Add state for uploaded files and connected services
  const [uploadedFiles] = useState([
    { name: 'Skyflow_2024_Roadmap.pdf', size: '2.4 MB', type: 'pdf', uploadTime: '2 hours ago' },
    { name: 'Privacy_Infrastructure_Q3.xlsx', size: '856 KB', type: 'excel', uploadTime: '1 day ago' },
    { name: 'Technical_Architecture.json', size: '124 KB', type: 'json', uploadTime: '3 days ago' }
  ]);
  
  const [connectedServices] = useState(['Jira', 'Confluence', 'GitHub']);

  // Pre-fill form with Skyflow data on component load
  useEffect(() => {
    const skyflowData = formPresets.sampleCompanies[0]; // Skyflow is the only company in the list now
    const industryData = formPresets.industries[0]; // Data privacy is the first industry
    
    setFormData({
      companyName: skyflowData.name,
      industry: industryData.name,
      companyDescription: "Founded in 2019, Skyflow provides Data Privacy Vaults delivered via a simple and elegant API. Inspired by the zero trust Data Privacy Vaults pioneered by companies like Apple and Netflix, Skyflow helps businesses handle financial, healthcare and other personal data safely and securely.",
      companyWebsite: 'https://www.skyflow.com/',
      objective: skyflowData.objectives,
      challenges: skyflowData.challenges,
      constraints: 'Budget limitations of $8M annually, 12-month timeline for global expansion, privacy regulations compliance',
      successCriteria: 'Zero data breaches, 99.99% uptime for PII vaults, compliance with all global privacy regulations, improved data utility while maintaining privacy',
      timeline: '12 months',
      budget: '$500K - $1M',
      roadmapFile: null
    });
    setSelectedIndustry(industryData.id);
  }, []);

  const handleInputChange = (field: keyof RoadmapData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIndustrySelect = (industry: any) => {
    setSelectedIndustry(industry.id);
    setFormData(prev => ({ ...prev, industry: industry.name }));
    setShowDropdown(false);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFormData(prev => ({ ...prev, roadmapFile: e.dataTransfer.files[0] }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, roadmapFile: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Pass form data through URL parameters
    const params = new URLSearchParams({
      company: formData.companyName,
      industry: formData.industry,
      objective: formData.objective.slice(0, 100), // Limit length for URL
      challenges: formData.challenges.slice(0, 100)
    });
    
    // Navigate directly to TRIZ Innovation Workflow
    navigate(`/business-model?${params.toString()}`);
  };

  const isFormValid = formData.companyName && formData.objective && formData.challenges;

  // Main Form View
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      {/* Enhanced Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 100, 0],
              y: [0, -50, 0],
              rotate: [0, 180, 360]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-20 left-20 w-32 h-32 rounded-full blur-xl" style={{ background: 'linear-gradient(90deg, rgba(66, 102, 83, 0.2), rgba(13, 86, 48, 0.2))' }}
          />
          <motion.div
            animate={{ 
              x: [0, -80, 0],
              y: [0, 100, 0],
              rotate: [360, 180, 0]
            }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute top-60 right-32 w-24 h-24 rounded-full blur-xl" style={{ background: 'linear-gradient(90deg, rgba(212, 175, 55, 0.2), rgba(184, 134, 11, 0.2))' }}
          />
          <motion.div
            animate={{ 
              x: [0, 60, 0],
              y: [0, -80, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-40 left-1/3 w-20 h-20 rounded-full blur-xl" style={{ background: 'linear-gradient(90deg, rgba(66, 102, 83, 0.2), rgba(13, 86, 48, 0.2))' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-8 pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
                                      {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-7xl sqsft-heading-1 mb-6 leading-tight"
            >
              Roadmap AI
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl md:text-3xl sqsft-body max-w-4xl mx-auto mb-8 font-light leading-relaxed" style={{ color: 'rgb(var(--color-text-secondary))' }}
             >
               Transform your data privacy strategy with{' '}
               <span className="sqsft-gradient-text font-semibold">
                 AI-powered innovation
               </span>{' '}
               using advanced TRIZ methodology
            </motion.p>

            {/* Value Propositions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-6 mb-12"
            >
                             {[
                 { icon: Zap, text: "10x Faster Innovation", color: "sqsft-gradient-primary" },
                 { icon: TrendingUp, text: "Data-Driven Insights", color: "sqsft-gradient-primary" },
                 { icon: Award, text: "TRIZ Methodology", color: "sqsft-gradient-primary" },
                 { icon: Shield, text: "Enterprise Ready", color: "sqsft-gradient-primary" }
               ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center sqsft-card backdrop-blur-xl px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center mr-3`}>
                    <item.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm sqsft-body font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              {/* Button removed */}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-5xl mx-auto px-8 pb-20">
        {/* Breadcrumb Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-2xl p-4 border border-white/50 dark:border-slate-700/50 shadow-lg">
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex items-center space-x-2" style={{ color: 'rgb(var(--color-sage-green))' }}>
                <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}>
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                </div>
                <span className="font-semibold">1. Roadmap Input</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center space-x-2 text-gray-400">
                <div className="w-4 h-4 rounded-full border-2 border-gray-400"></div>
                <span>2. Innovation Workflow</span>
              </div>
            </div>
          </div>
        </motion.div>
        {/* Form Header with Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl sqsft-heading-2 mb-4">
            Let's Build Your Innovation Roadmap
          </h2>
          <p className="text-lg sqsft-body mb-8" style={{ color: 'rgb(var(--color-text-secondary))' }}>
            Fill in the information below to generate AI-powered innovations tailored to your needs
          </p>
          
          {/* Progress Indicator */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="flex items-center">
              <div className="w-10 h-10 sqsft-gradient-primary text-white rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Company Info</span>
            </div>
            <div className="w-16 h-1 bg-gray-300 dark:bg-gray-600"></div>
            <div className="flex items-center">
              <div className="w-10 h-10 sqsft-gradient-primary text-white rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Goals & Context</span>
            </div>
            <div className="w-16 h-1 bg-gray-300 dark:bg-gray-600"></div>
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <span className="ml-3 text-sm font-medium text-gray-500 dark:text-gray-500">Upload (Optional)</span>
            </div>
          </div>
          
          {/* Helper Note */}
          <div className="inline-flex items-center px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)' }}>
            <Info className="w-4 h-4 mr-2" style={{ color: 'rgb(var(--color-sage-green))' }} />
            <span className="text-sm" style={{ color: 'rgb(var(--color-sage-green))' }}>Fields marked with * are required</span>
          </div>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Company Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="group relative"
          >
            <div className="absolute -inset-1 sqsft-gradient-primary rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-10 border border-white/50 dark:border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center mr-4">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Step 1: Tell Us About Your Company</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Basic information to understand your business context</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center px-4 py-2 rounded-xl" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)' }}>
                  <span className="text-sm font-medium" style={{ color: 'rgb(var(--color-sage-green))' }}>Required Section</span>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        What's Your Company Name? *
                      </label>
                      <span title="Your registered business name">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      </span>
                    </div>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      placeholder="e.g., Skyflow Inc."
                      className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400"
                      style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                      onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                      onBlur={(e) => e.target.style.borderColor = ''}
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      This helps us personalize your innovation roadmap
                    </p>
                  </div>
                  
                  <div className="relative space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Which Industry Are You In? *
                      </label>
                      <span title="Select your primary industry vertical">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      </span>
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-left flex items-center justify-between"
                        style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                        onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                        onBlur={(e) => e.target.style.borderColor = ''}
                      >
                        <span className={formData.industry ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
                          {formData.industry || 'Select your primary industry'}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 w-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl z-20 max-h-60 overflow-y-auto"
                        >
                          {formPresets.industries.map((industry) => (
                            <button
                              key={industry.id}
                              type="button"
                              onClick={() => handleIndustrySelect(industry)}
                              className="w-full px-6 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">{industry.name}</div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{industry.description}</div>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Industry-specific TRIZ principles will be applied
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Describe Your Company's Mission & Current State
                    </label>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Optional but recommended</span>
                  </div>
                  <textarea
                    value={formData.companyDescription}
                    onChange={(e) => handleInputChange('companyDescription', e.target.value)}
                    placeholder="Tell us about your company's mission, current products/services, team size, growth stage, and any unique aspects that would help us understand your context better..."
                    className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                    style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                    onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    rows={4}
                  />
                  <div className="flex items-start space-x-2 mt-2">
                    <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <strong>Pro tip:</strong> Include details about your target market, competitive landscape, and unique value proposition for more tailored innovations
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Company Website
                    </label>
                    <span title="Your company's official website">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                    </span>
                  </div>
                  <input
                    type="url"
                    value={formData.companyWebsite}
                    onChange={(e) => handleInputChange('companyWebsite', e.target.value)}
                    placeholder="e.g., https://www.skyflow.com"
                    className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400"
                    style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                    onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                    onBlur={(e) => e.target.style.borderColor = ''}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This helps us gather more context about your products and services.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Innovation Goals & Context */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="group relative"
          >
            <div className="absolute -inset-1 sqsft-gradient-primary rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-10 border border-white/50 dark:border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center mr-4">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Step 2: Define Your Innovation Vision</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">What problems are you solving and what success looks like</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center px-4 py-2 rounded-xl" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)' }}>
                  <span className="text-sm font-medium" style={{ color: 'rgb(var(--color-sage-green))' }}>Core Requirements</span>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      What's Your Primary Innovation Goal? *
                    </label>
                                          <span title="Be specific about what you want to achieve">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      </span>
                  </div>
                  <textarea
                    value={formData.objective}
                    onChange={(e) => handleInputChange('objective', e.target.value)}
                    placeholder="Example: 'We want to expand our data privacy platform globally while maintaining 99.99% uptime, achieving SOC2 compliance in all regions, and reducing operational costs by 30%. Our goal is to become the leading privacy infrastructure provider for enterprises handling sensitive data.'"
                    className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                    style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                    onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    rows={5}
                    required
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                      <span>Include measurable targets</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                      <span>Specify timeframes</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                      <span>Define success metrics</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      What Are Your Biggest Challenges? *
                    </label>
                                          <span title="Technical, business, or operational hurdles">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      </span>
                  </div>
                  <textarea
                    value={formData.challenges}
                    onChange={(e) => handleInputChange('challenges', e.target.value)}
                    placeholder="Example: 'Our main challenges include: 1) Scaling infrastructure to handle 10x growth while maintaining data residency compliance, 2) Integrating with legacy enterprise systems that have varying security protocols, 3) Reducing latency for global users while ensuring data never leaves regional boundaries, 4) Balancing ease of use with enterprise-grade security requirements.'"
                    className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                    style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                    onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                    onBlur={(e) => e.target.style.borderColor = ''}
                    rows={5}
                    required
                  />
                  <div className="rounded-xl p-4 mt-3" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)' }}>
                    <div className="flex items-start space-x-3">
                      <Zap className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(var(--color-royal-gold))' }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'rgb(var(--color-royal-gold))' }}>
                          Be specific about technical challenges
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'rgb(var(--color-royal-gold))' }}>
                          AI works best when it understands your technical constraints, scalability issues, integration challenges, and compliance requirements
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      What Are Your Constraints?
                    </label>
                    <textarea
                      value={formData.constraints}
                      onChange={(e) => handleInputChange('constraints', e.target.value)}
                      placeholder="Budget limits, team size, technology stack requirements, regulatory requirements, timeline restrictions..."
                      className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                      style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                      onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                      onBlur={(e) => e.target.style.borderColor = ''}
                      rows={4}
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      How Will You Measure Success?
                    </label>
                    <textarea
                      value={formData.successCriteria}
                      onChange={(e) => handleInputChange('successCriteria', e.target.value)}
                      placeholder="KPIs, metrics, milestones, business outcomes that indicate successful innovation..."
                      className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                      style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                      onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                      onBlur={(e) => e.target.style.borderColor = ''}
                      rows={4}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Expected Project Timeline
                    </label>
                    <select
                      value={formData.timeline}
                      onChange={(e) => handleInputChange('timeline', e.target.value)}
                      className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white"
                      style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                      onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                      onBlur={(e) => e.target.style.borderColor = ''}
                    >
                      <option value="">Select project timeline</option>
                      <option value="3 months">3 months (Quick wins)</option>
                      <option value="6 months">6 months (Standard)</option>
                      <option value="12 months">12 months (Comprehensive)</option>
                      <option value="18 months">18 months (Enterprise)</option>
                      <option value="2+ years">2+ years (Long-term vision)</option>
                    </select>
                  </div>
                  
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Innovation Budget Range
                    </label>
                    <select
                      value={formData.budget}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      className="w-full px-6 py-4 bg-white/80 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 rounded-2xl focus:ring-4 transition-all duration-200 text-gray-900 dark:text-white"
                      style={{ '--tw-ring-color': 'rgba(66, 102, 83, 0.2)' } as React.CSSProperties}
                      onFocus={(e) => e.target.style.borderColor = 'rgb(var(--color-sage-green))'}
                      onBlur={(e) => e.target.style.borderColor = ''}
                    >
                      <option value="">Select budget range</option>
                      <option value="<$50K">Less than $50K (Pilot)</option>
                      <option value="$50K - $100K">$50K - $100K (Small)</option>
                      <option value="$100K - $500K">$100K - $500K (Medium)</option>
                      <option value="$500K - $1M">$500K - $1M (Large)</option>
                      <option value="$1M+">$1M+ (Enterprise)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Current Roadmap Upload */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="group relative"
          >
            <div className="absolute -inset-1 sqsft-gradient-gold rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div className="relative bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-10 border border-white/50 dark:border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 sqsft-gradient-gold rounded-2xl flex items-center justify-center mr-4">
                    <Upload className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Step 3: Share Your Existing Roadmap</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Optional: Upload for AI to analyze and enhance</p>
                  </div>
                </div>
                {/* Status badge showing active uploads */}
                <div className="hidden md:flex items-center px-4 py-2 rounded-xl" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)' }}>
                  <div className="w-2 h-2 rounded-full animate-pulse mr-2" style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}></div>
                  <span className="text-sm font-medium" style={{ color: 'rgb(var(--color-sage-green))' }}>3 files uploaded</span>
                </div>
              </div>
              
              {/* Upload Area - Now at the top */}
              <div
                className={`border-3 border-dashed rounded-3xl p-16 text-center transition-all duration-300 mb-8 ${
                  dragActive 
                    ? 'scale-102' 
                    : 'border-slate-300 dark:border-slate-600 hover:bg-gray-50/50 dark:hover:bg-gray-900/10'
                }`}
                style={{
                  borderColor: dragActive ? 'rgb(var(--color-sage-green))' : undefined,
                  backgroundColor: dragActive ? 'rgba(66, 102, 83, 0.1)' : undefined
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="w-20 h-20 sqsft-gradient-gold rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg"
                >
                  <Upload className="w-10 h-10 text-white" />
                </motion.div>
                
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                    Upload Your Roadmap
                  </h3>
                  <p className="text-lg text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    Drag and drop your file here, or click to browse for enhanced analysis
                  </p>
                  <div className="flex flex-wrap justify-center gap-3 mt-6">
                    {['PDF', 'Excel', 'JSON', 'Images'].map((format) => (
                      <span key={format} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg text-sm">
                        {format}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                    💡 AI can generate innovations based on your company info and objectives alone
                  </p>
                </div>
                
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="roadmap-upload"
                  accept=".json,.xlsx,.xls,.pdf,.png,.jpg,.jpeg"
                />
                <label
                  htmlFor="roadmap-upload"
                  className="inline-block mt-8 px-8 py-4 sqsft-btn-primary text-white font-semibold rounded-2xl transition-all duration-200 cursor-pointer shadow-lg hover:shadow-xl hover:scale-105"
                >
                  Choose File
                </label>

                {/* External Connectors Section */}
                <div className="mt-12 pt-10 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-6">
                    Or Import From External Sources
                  </h4>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-3xl mx-auto">
                    {[
                      { 
                        name: 'Jira', 
                        connected: true,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                            <path d="M12.234 1.168l10.59 10.59-10.59 10.59L1.645 11.758z" fill="#2684FF"></path><path d="M12.234 6.728L6.675 12.287l5.56 5.559 5.558-5.559z" fill="#fff"></path>
                          </svg>
                        ), 
                        color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      },
                      { 
                        name: 'Confluence', 
                        connected: true,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                           <path d="M11.66 13.84L8.14 3.92a1 1 0 0 1-.22-.59a1 1 0 0 1 .37-.74a1 1 0 0 1 1.2-.23l3.48 1.23c.3.1.57.29.78.53c1.47 1.66 2.37 3.82 2.47 6.1c.11 2.5-.72 4.9-2.33 6.6a1 1 0 0 1-1.29.28l-3.5-1.23a.97.97 0 0 1-.72-.73c-.08-2.43-1.07-4.63-2.62-6.2Z" fill="#2684FF"></path><path d="M21.21 13.29a.98.98 0 0 1-.3.42c-.99.88-2.25 1.48-3.64 1.74a7.83 7.83 0 0 1-5.32-1.63a.99.99 0 0 1-.29-.39L2.8 3.51a1 1 0 0 1 .37-1.33a.99.99 0 0 1 .4-.1a1 1 0 0 1 .9.67l3.52 9.92c.07.2.17.38.3.54c.73.9 1.7 1.61 2.8 2.02c1.7.63 3.58.5 5.17-.38a.98.98 0 0 1 .48-.17l3.48-1.23a1 1 0 0 1 1.15.84a1 1 0 0 1-.41 1.07Z" fill="#2684FF"></path>
                          </svg>
                        ), 
                        color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      },
                      { 
                        name: 'Slack', 
                        connected: false,
                        icon: <Slack className="w-6 h-6" />, 
                        color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' 
                      },
                      { 
                        name: 'GitHub', 
                        connected: true,
                        icon: <Github className="w-6 h-6" />, 
                        color: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800' 
                      },
                      { 
                        name: 'GitLab', 
                        connected: false,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                            <path d="M23.59 13.56L23.3 12.64L20.33 3.23C20.24 2.96 20.02 2.76 19.76 2.7C19.49 2.65 19.21 2.76 19.05 2.97C19.05 2.97 19.05 2.97 19.04 2.97L16.18 7.06H7.82L4.96 2.96C4.8 2.75 4.53 2.63 4.26 2.69C3.99 2.75 3.77 2.95 3.68 3.22L0.7 12.63L0.41 13.55C0.17 14.31 0.3 15.12 0.77 15.77L11.98 22.87L23.23 15.78C23.7 15.13 23.83 14.32 23.59 13.56Z" fill="#E24329"/>
                            <path d="M12 22.87L16.18 7.06H7.82L12 22.87Z" fill="#FC6D26"/>
                            <path d="M12 22.87L7.82 7.06H0.7L12 22.87Z" fill="#FCA326"/>
                            <path d="M0.7 12.63L0.41 13.55C0.17 14.31 0.3 15.12 0.77 15.77L12 22.87L0.7 12.63Z" fill="#E24329"/>
                            <path d="M0.7 12.63L7.82 7.06H0.7L0.7 12.63Z" fill="#FC6D26"/>
                            <path d="M12 22.87L16.18 7.06H23.3L12 22.87Z" fill="#FCA326"/>
                            <path d="M23.3 12.63L23.59 13.55C23.83 14.31 23.7 15.12 23.23 15.77L12 22.87L23.3 12.63Z" fill="#E24329"/>
                            <path d="M23.3 12.63L16.18 7.06H23.3V12.63Z" fill="#FC6D26"/>
                          </svg>
                        ), 
                        color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' 
                      },
                      { 
                        name: 'Asana', 
                        connected: false,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                            <path d="M19.0673 11.9996C19.0673 13.0703 18.1967 13.9409 17.126 13.9409C16.0553 13.9409 15.1847 13.0703 15.1847 11.9996C15.1847 10.9289 16.0553 10.0583 17.126 10.0583C18.1967 10.0583 19.0673 10.9289 19.0673 11.9996Z" fill="#F06A6A"/>
                            <path d="M8.8153 13.9409C9.88601 13.9409 10.7566 13.0703 10.7566 11.9996C10.7566 10.9289 9.88601 10.0583 8.8153 10.0583C7.74459 10.0583 6.87402 10.9289 6.87402 11.9996C6.87402 13.0703 7.74459 13.9409 8.8153 13.9409Z" fill="#F06A6A"/>
                            <path d="M12.9716 8.12C12.9716 9.19071 12.101 10.0613 11.0303 10.0613C9.95958 10.0613 9.08901 9.19071 9.08901 8.12C9.08901 7.04929 9.95958 6.17871 11.0303 6.17871C12.101 6.17871 12.9716 7.04929 12.9716 8.12Z" fill="#F06A6A"/>
                          </svg>
                        ), 
                        color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                      },
                      { 
                        name: 'Trello', 
                        connected: false,
                        icon: <TrelloIcon className="w-6 h-6" />, 
                        color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      },
                      { 
                        name: 'Google Docs', 
                        connected: false,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#4285F4"/>
                            <path d="M14 2V8H20L14 2Z" fill="#A1C2FA"/>
                            <path d="M7 13H17V14.5H7V13Z" fill="white"/>
                            <path d="M7 16H14V17.5H7V16Z" fill="white"/>
                            <path d="M7 10H17V11.5H7V10Z" fill="white"/>
                          </svg>
                        ), 
                        color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      },
                      { 
                        name: 'Notion', 
                        connected: false,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                            <path d="M4.35704 4.61975C4.35704 4.23535 4.68288 3.92615 5.08478 3.92615L6.83286 3.92827C7.09551 3.92827 7.34277 4.05463 7.49526 4.27223L8.66125 5.88146L17.5266 3.92827C17.8524 3.85483 18.1782 4.00679 18.3307 4.2984L19.8639 6.85843C20.0164 7.15003 19.9906 7.51893 19.8123 7.78582L19.3845 8.40502L17.4037 11.3382L16.3947 12.8333L15.0471 14.8863C14.9983 14.9597 14.9632 15.0393 14.9361 15.1212L14.0047 18.5143C13.9405 18.7301 13.777 18.9079 13.5567 18.9852L12.1316 19.4848C11.9177 19.5602 11.684 19.5358 11.4909 19.4193L8.94849 17.9425C8.84992 17.8812 8.76457 17.7974 8.70152 17.6965L6.64028 13.9948C6.61528 13.95 6.59751 13.903 6.58677 13.8541L6.22788 11.7936L4.37269 5.04431C4.36197 4.9048 4.35704 4.76316 4.35704 4.61975ZM6.94876 5.03788L5.66153 5.03591C5.53728 5.03591 5.43658 5.13662 5.43658 5.26087V5.35178C5.43658 5.3763 5.43871 5.40082 5.44363 5.42534L7.21931 11.9159C7.23004 11.9609 7.24781 12.004 7.2728 12.0449L9.30644 15.6955C9.35832 15.7943 9.43178 15.8762 9.52682 15.9337L11.9743 17.3588C12.0674 17.4163 12.1825 17.4282 12.2876 17.3884L13.5786 16.9329C13.6837 16.8931 13.7631 16.8054 13.7906 16.6978L14.7367 13.1735C14.7563 13.0909 14.7895 13.0103 14.8406 12.937L16.7742 10.0337L18.4878 7.48747C18.5585 7.38013 18.5585 7.24563 18.4878 7.13828L17.6101 5.66153C17.5768 5.61239 17.533 5.57235 17.4799 5.54592L8.66835 7.48747C8.4978 7.52592 8.31518 7.44248 8.21941 7.28873L6.94876 5.03788Z" fill="#000000"/>
                          </svg>
                        ), 
                        color: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800' 
                      },
                      { 
                        name: 'Monday', 
                        connected: false,
                        icon: (
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                            <rect x="2" y="7" width="4" height="10" rx="2" fill="#FF3D57"/>
                            <rect x="10" y="7" width="4" height="10" rx="2" fill="#FFCB00"/>
                            <rect x="18" y="7" width="4" height="10" rx="2" fill="#00D647"/>
                          </svg>
                        ), 
                        color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' 
                      }
                    ].map((connector) => (
                      <motion.button
                        key={connector.name}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        className={`relative flex flex-col items-center justify-center p-4 rounded-xl border ${connector.color} hover:shadow-md transition-all duration-200 ${
                          connector.connected ? 'ring-2 ring-offset-2 dark:ring-offset-slate-800' : ''
                        }`}
                        style={{
                          '--tw-ring-color': connector.connected ? 'rgb(var(--color-sage-green))' : undefined
                        } as React.CSSProperties}
                        onClick={() => alert(`${connector.name} integration ${connector.connected ? 'settings' : 'coming soon'}!`)}
                      >
                        {connector.connected && (
                          <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}>
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <div className="mb-2 flex items-center justify-center h-8">
                          {connector.icon}
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{connector.name}</span>
                        {connector.connected && (
                          <span className="text-xs mt-1" style={{ color: 'rgb(var(--color-sage-green))' }}>Connected</span>
                        )}
                      </motion.button>
                    ))}
                  </div>
                  
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-6">
                    Securely connect to your existing tools to import roadmap data
                  </p>
                </div>
              </div>
              
              {/* Uploaded Files Section */}
              <div className="mb-8 bg-slate-50 dark:bg-slate-700/50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-slate-600 dark:text-slate-400" />
                  Uploaded Documents
                </h3>
                <div className="space-y-3">
                  {uploadedFiles.map((file, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600"
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${
                          file.type === 'pdf' ? 'bg-red-100 dark:bg-red-900/30' :
                          file.type === 'excel' ? 'bg-green-100 dark:bg-green-900/30' :
                          'bg-blue-100 dark:bg-blue-900/30'
                        }`}>
                          {file.type === 'pdf' ? (
                            <FileDocument className="w-5 h-5 text-red-600 dark:text-red-400" />
                          ) : file.type === 'excel' ? (
                            <FileSpreadsheet className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <FileCode className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 dark:text-gray-200">{file.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{file.size} • {file.uploadTime}</p>
                        </div>
                      </div>
                      <CheckCircle2 className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Connected Services Section */}
              <div className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)' }}>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                  <Zap className="w-5 h-5 mr-2" style={{ color: 'rgb(var(--color-sage-green))' }} />
                  Connected Services
                </h3>
                <div className="flex flex-wrap gap-3">
                  {connectedServices.map((service, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2 + 0.1 * index }}
                      className="flex items-center bg-white dark:bg-slate-800 px-4 py-2 rounded-xl border"
                      style={{ borderColor: 'rgba(66, 102, 83, 0.3)' }}
                    >
                      <div className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{service}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">synced</span>
                    </motion.div>
                  ))}
                  <p className="text-sm italic" style={{ color: 'rgb(var(--color-sage-green))' }}>
                    Last sync: 5 minutes ago
                  </p>
                </div>
              </div>
              
              {formData.roadmapFile && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  className="mt-8 p-6 rounded-2xl border"
                  style={{ 
                    background: 'linear-gradient(to right, rgba(66, 102, 83, 0.1), rgba(13, 86, 48, 0.1))',
                    borderColor: 'rgba(66, 102, 83, 0.3)'
                  }}
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 sqsft-gradient-primary rounded-xl flex items-center justify-center mr-4">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg" style={{ color: 'rgb(var(--color-sage-green))' }}>
                        {formData.roadmapFile.name}
                      </p>
                      <p className="text-sm" style={{ color: 'rgb(var(--color-sage-green))' }}>
                        Ready for AI analysis
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Enhanced Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <motion.button
              type="submit"
              disabled={!isFormValid}
              whileHover={isFormValid ? { scale: 1.05, y: -2 } : {}}
              whileTap={isFormValid ? { scale: 0.98 } : {}}
              className={`
                                 group relative px-16 py-6 rounded-3xl font-bold text-xl transition-all duration-300 flex items-center mx-auto shadow-2xl
                 ${isFormValid
                   ? 'sqsft-btn-primary text-white'
                   : 'bg-slate-300 dark:bg-slate-600 text-slate-500 cursor-not-allowed'
                 }
              `}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <Lightbulb className="w-7 h-7 mr-4 group-hover:animate-pulse" />
              Generate AI Innovations
              <ArrowRight className="w-7 h-7 ml-4 group-hover:translate-x-2 transition-transform duration-300" />
            </motion.button>
            
            {!isFormValid && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 p-4 rounded-2xl border max-w-md mx-auto"
                style={{ 
                  backgroundColor: 'rgba(212, 175, 55, 0.1)',
                  borderColor: 'rgba(212, 175, 55, 0.3)'
                }}
              >
                <p className="font-medium" style={{ color: 'rgb(var(--color-royal-gold))' }}>
                  📝 Please complete all required fields: Company Name, Innovation Objective, and Challenges
                </p>
              </motion.div>
            )}
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
                             className="mt-6 text-gray-600 dark:text-gray-400"
            >
              ⚡ Powered by advanced AI and TRIZ methodology
            </motion.p>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

export default RoadmapInnovation;